import { createFileRoute, useNavigate, Link, redirect } from '@tanstack/react-router'
import { assetPath } from '@/utils/assetPath'
import { useState, useEffect, useCallback, useRef } from 'react'
import { ArrowLeft, Check, AlertCircle } from 'lucide-react'
import { GlossyButton } from '@/components/shared/atoms/GlossyButton'
import { GlossyInput } from '@/components/auth/GlossyInput'
import { FormErrorMessage } from '@/components/auth/FormErrorMessage'
import { FadeInUp } from '@/components/shared/atoms/FadeInUp'
import { useOnboardingStore, waitForOnboardingHydration } from '@/stores/onboarding'
import { validateUsername } from '@/utils/validation'
import { useCheckUsernameAvailability } from '@/hooks/api/useUsers'
import useDebounce from '@/hooks/useDebounce'

export const Route = createFileRoute('/(onboarding)/(auth)/register/username')({
    validateSearch: (search) => ({
        returnToPath:
            typeof search.returnToPath === 'string'
                ? search.returnToPath
                : undefined,
        error:
            typeof search.error === 'string'
                ? search.error
                : undefined,
    }),
    beforeLoad: async () => {
        // Wait for store hydration from sessionStorage before checking state
        await waitForOnboardingHydration()
        const { registrationData } = useOnboardingStore.getState()
        const hasEmailFlow = registrationData.email && registrationData.fullName
        const hasSocialFlow = registrationData.authMethod === 'google' || registrationData.authMethod === 'apple'
        if (!hasEmailFlow && !hasSocialFlow) {
            throw redirect({ to: '/register', search: { returnToPath: undefined, error: 'missing-basic-info' } })
        }
    },
    component: RegisterUsernameRoute,
})

function RegisterUsernameRoute() {
    const {
        registrationData,
        setRegistrationData,
        markStepCompleted,
        resetRegistrationData,
    } = useOnboardingStore()
    const navigate = useNavigate()
    const { returnToPath } = Route.useSearch()

    const [username, setUsername] = useState(registrationData.username || '')
    const [validationResult, setValidationResult] = useState(() =>
        validateUsername(username)
    )
    const debouncedUsername = useDebounce(username, 1000)
    const [apiError, setApiError] = useState<string | null>(null) // Keep for availability check errors
    const usernameInputRef = useRef<HTMLInputElement>(null)

    // Only need username check hook here
    const {
        mutate: checkUsername,
        isPending: isCheckingUsername,
        data: availabilityData,
        error: checkUsernameError,
        reset: resetCheckUsername,
    } = useCheckUsernameAvailability()

    const isLoading = isCheckingUsername
    const isSocialSignIn = registrationData.authMethod === 'google' || registrationData.authMethod === 'apple'

    // Refocus username input when fetching is done
    useEffect(() => {
        if (!isCheckingUsername && usernameInputRef.current) {
            usernameInputRef.current.focus()
        }
    }, [isCheckingUsername])

    // When username changes and passes basic validation, check its availability
    useEffect(() => {
        setApiError(null) // Clear API error on username change
        if (debouncedUsername && validateUsername(debouncedUsername).isValid) {
            checkUsername(debouncedUsername)
        } else {
            resetCheckUsername() // Reset availability check if username is invalid/short
        }
    }, [debouncedUsername, checkUsername, resetCheckUsername])

    // Combine client-side and server-side validation
    useEffect(() => {
        const clientValidation = validateUsername(username)
        let serverAvailable = true
        let serverMessage: string | undefined

        if (availabilityData) {
            serverAvailable = availabilityData.isAvailable
            serverMessage = availabilityData.isAvailable
                ? undefined
                : 'This username is already taken'
        }

        if (checkUsernameError) {
            // Handle error from availability check
            serverAvailable = false // Treat error as unavailable for safety
            serverMessage =
                'Could not check username availability. Please try again.'
            setApiError(serverMessage)
        }

        if (clientValidation.isValid && serverAvailable) {
            setValidationResult({ isValid: true })
        } else {
            setValidationResult({
                isValid: false,
                errorMessage: !clientValidation.isValid
                    ? clientValidation.errorMessage
                    : serverMessage,
            })
        }
    }, [username, availabilityData, checkUsernameError])

    const handleBack = useCallback(() => {
        // If the user came via social auth (Google or Apple), reset the registration data
        // before navigating back. This prevents an immediate redirect
        // back to this username page from the RegisterFormPage.
        if (isSocialSignIn) {
            resetRegistrationData()
        }
        // Navigate back to the main registration form page.
        navigate({ to: '/register', search: { returnToPath, error: undefined } })
    }, [navigate, isSocialSignIn, resetRegistrationData, returnToPath])

    const handleContinue = useCallback(() => {
        if (!username || !validationResult.isValid || isLoading) return
        setRegistrationData({ username })
        markStepCompleted('username')
        // Navigate to the next step: Account Type Selection
        navigate({ to: '/register/preferences', search: { returnToPath, error: undefined } })
    }, [
        username,
        validationResult.isValid,
        isLoading,
        setRegistrationData,
        markStepCompleted,
        navigate,
        returnToPath,
    ])

    // Determine if the 'Continue' button should be disabled
    const isContinueDisabled =
        isLoading || !validationResult.isValid || !username

    return (
        <section className="relative z-10 flex min-h-dvh flex-col pt-safe">
            <div className="flex px-2 pt-3 md:justify-center">
                <img
                    alt="logo-red"
                    src={assetPath('logo/logo-red.svg')}
                    className="h-[34px] w-[155px] md:mt-12 md:h-[100px] md:w-[440px]"
                />
            </div>

            <FadeInUp delay={0.3}>
                <div className="px-4 pt-2 pb-6 md:pt-[35vh] md:text-center">
                    <h1 className="font-sfpro text-28 leading-8 font-semibold text-white">
                        Create your
                        <br className="md:hidden" />
                        <span className="text-white-48"> username</span>
                    </h1>
                </div>
            </FadeInUp>

            <div className="flex flex-1 flex-col px-2">
                <FadeInUp delay={0.5}>
                    <div>
                        <GlossyInput
                            ref={usernameInputRef}
                            placeholder="Username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            suffix={
                                validationResult.isValid &&
                                    username.length > 0 &&
                                    !isCheckingUsername ? (
                                    <Check
                                        color="white"
                                        width={20}
                                        height={20}
                                        strokeWidth={1}
                                    />
                                ) : null
                            }
                            glassEffect={{
                                opacity: 'bg-white/20',
                                blur: 'backdrop-blur-md',
                            }}
                            focusedBorderColor={
                                !validationResult.isValid && username.length > 0
                                    ? 'border-red-500'
                                    : 'border-white'
                            }
                            unfocusedBorderColor={
                                !validationResult.isValid && username.length > 0
                                    ? 'border-red-500/70'
                                    : 'border-white/30'
                            }
                        />

                        {isCheckingUsername && username.length >= 3 && (
                            <p className="mt-1 animate-pulse text-center text-xs font-normal text-white">
                                Checking availability...
                            </p>
                        )}

                        {!isCheckingUsername &&
                            availabilityData &&
                            validationResult.isValid &&
                            username.length > 0 && (
                                <p className="mt-1 text-center text-xs font-normal text-white">
                                    Username available!
                                </p>
                            )}

                        {/* Client/Server Validation Error */}
                        {!validationResult.isValid && username.length > 0 && (
                            <FormErrorMessage
                                message={validationResult.errorMessage || ''}
                                center
                                className="mt-1 text-xs"
                            />
                        )}

                        {/* Display API Error from check */}
                        {apiError && !validationResult.isValid && (
                            <div className="mt-3 flex items-center justify-center gap-2 rounded-lg bg-red-500/20 p-2 backdrop-blur">
                                <AlertCircle
                                    className="shrink-0 text-red-500"
                                    size={16}
                                />
                                <p className="text-center text-xs text-red-400">
                                    {apiError}
                                </p>
                            </div>
                        )}
                    </div>
                </FadeInUp>

                <FadeInUp delay={0.7} className="mt-auto mb-6">
                    <div className="flex items-center gap-2 pt-6">
                        <GlossyButton
                            variant="ghost"
                            className="rounded-xl bg-white/20 px-4 py-[14px] text-sm font-medium text-white backdrop-blur-md"
                            onClick={handleBack}
                            disabled={isLoading}
                        >
                            <ArrowLeft
                                strokeWidth={1}
                                className="mr-1 h-5 w-5"
                            />
                            Back
                        </GlossyButton>
                        <GlossyButton
                            className="w-full rounded-xl py-[14px] text-sm"
                            onClick={handleContinue}
                            disabled={isContinueDisabled}
                        >
                            {isLoading ? 'Checking...' : 'Continue'}
                        </GlossyButton>
                    </div>

                    <div className="mt-4 px-4 text-center">
                        <p className="text-sm font-normal text-white">
                            By creating an account, you agree that you have read
                            and accepted our{' '}
                            <Link
                                to="/terms-of-use"
                                className="font-semibold underline hover:text-white/80"
                            >
                                Terms of use.
                            </Link>
                        </p>
                    </div>
                </FadeInUp>
            </div>
        </section>
    )
}
